/**
 * Parse SF2 bytes into a compact runtime bank for TsAudioKernel.
 * Library (`soundfont2`) owns RIFF parsing only — playback stays in-kernel.
 */
import { SoundFont2, GeneratorType, type Generator, type Sample } from "soundfont2";
import type { Sf2PresetMeta, Sf2RuntimeBank, Sf2Zone } from "../types";
import { findSf2Font } from "./catalog";

export { findSf2Zone } from "./findZone";

function genValue(
  gens: Partial<Record<GeneratorType, Generator>>,
  type: GeneratorType,
  fallback = 0,
): number {
  const value = gens[type]?.value;
  return typeof value === "number" ? value : fallback;
}

function genRange(
  gens: Partial<Record<GeneratorType, Generator>>,
  type: GeneratorType,
  fallback: { lo: number; hi: number },
): { lo: number; hi: number } {
  const range = gens[type]?.range;
  if (range && typeof range.lo === "number" && typeof range.hi === "number") {
    return { lo: range.lo, hi: range.hi };
  }
  return fallback;
}

/** SF2 initialAttenuation is in centibels (0.1 dB). */
function attenuationToGain(centibels: number): number {
  const cb = Math.max(0, centibels);
  return Math.min(1, Math.pow(10, -cb / 200));
}

function decodeSample(sample: Sample): Float32Array {
  const src = sample.data;
  const out = new Float32Array(src.length);
  for (let i = 0; i < src.length; i++) {
    out[i] = (src[i] ?? 0) / 32768;
  }
  return out;
}

function zoneDedupeKey(zone: Sf2Zone): string {
  return [
    zone.bank,
    zone.program,
    zone.sampleIndex,
    zone.keyLo,
    zone.keyHi,
    zone.velLo,
    zone.velHi,
    zone.rootKey,
    zone.tuneCents,
    zone.sampleStart,
    zone.sampleEnd,
    zone.loopStart,
    zone.loopEnd,
    zone.loopMode,
  ].join(":");
}

/**
 * Build a runtime bank from raw SF2 bytes.
 */
export function parseSf2ToRuntimeBank(fontId: string, bytes: Uint8Array): Sf2RuntimeBank {
  const sf = new SoundFont2(bytes);

  const samples: Float32Array[] = sf.samples.map(decodeSample);
  const sampleIndex = new Map<Sample, number>();
  for (let i = 0; i < sf.samples.length; i++) {
    sampleIndex.set(sf.samples[i]!, i);
  }

  const presets: Sf2PresetMeta[] = sf.presets
    .filter(p => p.header.name !== "EOP")
    .map(p => ({
      bank: p.header.bank,
      program: p.header.preset,
      name: p.header.name.trim() || `Program ${p.header.preset}`,
    }))
    .sort((a, b) => a.bank - b.bank || a.program - b.program);

  const zonesByKey = new Map<string, Sf2Zone>();

  for (const preset of sf.presets) {
    if (preset.header.name === "EOP") {
      continue;
    }
    const bank = preset.header.bank;
    const program = preset.header.preset;

    for (let midi = 0; midi < 128; midi++) {
      const key = sf.getKeyData(midi, bank, program);
      if (!key) {
        continue;
      }
      const gens = key.generators;
      const sample = key.sample;
      const idx = sampleIndex.get(sample);
      if (idx === undefined) {
        continue;
      }

      const keyRange = genRange(gens, GeneratorType.KeyRange, { lo: 0, hi: 127 });
      const velRange = genRange(gens, GeneratorType.VelRange, { lo: 0, hi: 127 });
      const overridingRoot = genValue(gens, GeneratorType.OverridingRootKey, -1);
      const rootKey =
        overridingRoot >= 0 && overridingRoot <= 127
          ? overridingRoot
          : sample.header.originalPitch >= 0 && sample.header.originalPitch <= 127
            ? sample.header.originalPitch
            : 60;

      const coarse = genValue(gens, GeneratorType.CoarseTune, 0);
      const fine = genValue(gens, GeneratorType.FineTune, 0) + (sample.header.pitchCorrection ?? 0);
      const startOff =
        genValue(gens, GeneratorType.StartAddrsOffset, 0)
        + genValue(gens, GeneratorType.StartAddrsCoarseOffset, 0) * 32768;
      const endOff =
        genValue(gens, GeneratorType.EndAddrsOffset, 0)
        + genValue(gens, GeneratorType.EndAddrsCoarseOffset, 0) * 32768;
      const loopStartOff =
        genValue(gens, GeneratorType.StartLoopAddrsOffset, 0)
        + genValue(gens, GeneratorType.StartLoopAddrsCoarseOffset, 0) * 32768;
      const loopEndOff =
        genValue(gens, GeneratorType.EndLoopAddrsOffset, 0)
        + genValue(gens, GeneratorType.EndLoopAddrsCoarseOffset, 0) * 32768;

      const sampleLen = samples[idx]!.length;
      const sampleStart = Math.max(0, Math.min(sampleLen - 1, Math.floor(startOff)));
      const sampleEnd = Math.max(
        sampleStart + 1,
        Math.min(sampleLen, Math.floor(sampleLen + endOff)),
      );
      // Sample header loops are already relative to sample start (soundfont2).
      let loopStart = Math.floor((sample.header.startLoop ?? 0) + loopStartOff);
      let loopEnd = Math.floor((sample.header.endLoop ?? sampleLen) + loopEndOff);
      loopStart = Math.max(sampleStart, Math.min(loopStart, sampleEnd - 1));
      loopEnd = Math.max(loopStart + 1, Math.min(loopEnd, sampleEnd));

      const loopMode = genValue(gens, GeneratorType.SampleModes, 0) & 3;
      const gain = attenuationToGain(genValue(gens, GeneratorType.InitialAttenuation, 0));

      const zone: Sf2Zone = {
        bank,
        program,
        sampleIndex: idx,
        keyLo: keyRange.lo,
        keyHi: keyRange.hi,
        velLo: velRange.lo,
        velHi: velRange.hi,
        rootKey,
        tuneCents: coarse * 100 + fine,
        sampleRate: Math.max(1, sample.header.sampleRate || 44100),
        sampleStart,
        sampleEnd,
        loopStart,
        loopEnd,
        loopMode,
        gain,
      };
      zonesByKey.set(zoneDedupeKey(zone), zone);
    }
  }

  return {
    fontId,
    presets,
    samples,
    zones: [...zonesByKey.values()],
  };
}

/** Host-side cache so multiple slots sharing a font don't re-parse. */
const bankCache = new Map<string, Sf2RuntimeBank>();
const inflight = new Map<string, Promise<Sf2RuntimeBank>>();

export function getCachedSf2Bank(fontId: string): Sf2RuntimeBank | undefined {
  return bankCache.get(fontId);
}

export function clearSf2BankCache(): void {
  bankCache.clear();
  inflight.clear();
}

/**
 * Fetch + parse a catalog font (or absolute URL / raw bytes). Cached by fontId.
 */
export async function loadSf2RuntimeBank(
  fontId: string,
  options?: { url?: string; bytes?: Uint8Array },
): Promise<Sf2RuntimeBank> {
  const cached = bankCache.get(fontId);
  if (cached) {
    return cached;
  }
  const pending = inflight.get(fontId);
  if (pending) {
    return pending;
  }

  const promise = (async () => {
    let bytes = options?.bytes;
    if (!bytes) {
      const url = options?.url ?? findSf2Font(fontId)?.url;
      if (!url) {
        throw new Error(`Unknown soundfont id: ${fontId}`);
      }
      // Bun/tests: resolve lab-relative /assets paths from disk when fetch isn't available.
      if (url.startsWith("/assets/")) {
        const candidates = [
          `apps/audio-lab${url}`,
          `./apps/audio-lab${url}`,
        ];
        for (const candidate of candidates) {
          try {
            const file = Bun.file(candidate);
            if (await file.exists()) {
              bytes = new Uint8Array(await file.arrayBuffer());
              break;
            }
          } catch {
            // try next / fall through to fetch
          }
        }
      }
      if (!bytes) {
        // Prefer page-relative assets/ so production builds work off subdirectory / file:// roots.
        const fetchUrl =
          typeof window !== "undefined" && url.startsWith("/assets/")
            ? new URL(url.slice(1), window.location.href).href
            : url;
        const response = await fetch(fetchUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch soundfont ${fontId}: ${response.status}`);
        }
        bytes = new Uint8Array(await response.arrayBuffer());
      }
    }
    const bank = parseSf2ToRuntimeBank(fontId, bytes);
    bankCache.set(fontId, bank);
    return bank;
  })();

  inflight.set(fontId, promise);
  try {
    return await promise;
  } finally {
    inflight.delete(fontId);
  }
}

/** Clone bank samples so the host cache survives transferable postMessage. */
export function cloneSf2BankForTransfer(bank: Sf2RuntimeBank): {
  bank: Sf2RuntimeBank;
  transfer: Transferable[];
} {
  const samples = bank.samples.map(sample => {
    const copy = new Float32Array(sample.length);
    copy.set(sample);
    return copy;
  });
  return {
    bank: {
      fontId: bank.fontId,
      presets: bank.presets.map(p => ({ ...p })),
      samples,
      zones: bank.zones.map(z => ({ ...z })),
    },
    transfer: samples.map(s => s.buffer),
  };
}
